import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { checkUsernameAvailability } from '../../api/client';

const USERNAME_RE = /^[A-Za-z0-9._-]{3,32}$/;
type UsernameStatus = 'idle' | 'checking' | 'available' | 'unavailable' | 'invalid';

export default function Register() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [usernameStatus, setUsernameStatus] = useState<UsernameStatus>('idle');
  const [usernameHint, setUsernameHint] = useState('');
  const { register } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const value = username.trim();
    if (!value) {
      setUsernameStatus('idle');
      setUsernameHint('');
      return;
    }
    if (!USERNAME_RE.test(value) || value.includes('@')) {
      setUsernameStatus('invalid');
      setUsernameHint('Use 3-32 chars: letters, numbers, dot, underscore, hyphen.');
      return;
    }
    setUsernameStatus('checking');
    setUsernameHint('Checking availability...');
    const timer = setTimeout(async () => {
      try {
        const result = await checkUsernameAvailability(value);
        if (result.available) {
          setUsernameStatus('available');
          setUsernameHint(result.message || 'Username is available');
        } else {
          setUsernameStatus('unavailable');
          setUsernameHint(result.message || 'Username is not available');
        }
      } catch {
        setUsernameStatus('unavailable');
        setUsernameHint('Could not validate username right now');
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [username]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    const normalizedUsername = username.trim();
    if (!normalizedUsername) {
      setError('Username is required');
      return;
    }
    if (!USERNAME_RE.test(normalizedUsername) || normalizedUsername.includes('@')) {
      setError('Username must be 3-32 chars using letters, numbers, dot, underscore, hyphen.');
      return;
    }
    if (usernameStatus !== 'available') {
      setError('Please choose an available username.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    setLoading(true);
    try {
      await register(normalizedUsername, password);
      setTimeout(() => navigate('/'), 0);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Registration failed';
      setError(
        message.toLowerCase().includes('fetch') || message.toLowerCase().includes('network')
          ? 'Cannot reach server. Check your connection or try again later.'
          : message
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col bg-gradient-to-b from-amber-50 to-orange-50 pt-14">
      <div className="flex flex-1 items-center justify-center px-4">
      <div className="w-full max-w-sm">
        <div className="bg-[#6B3B18] rounded-2xl p-8 shadow-xl text-white">
          <div className="text-center mb-6">
            <img src="/logo.png" alt="Frequency Locus" className="w-16 h-16 mx-auto mb-3 rounded-full object-contain bg-white/10 p-1" />
            <h1 className="text-2xl font-bold">Create account</h1>
            <p className="text-amber-100/90 text-sm mt-1">Frequency Locus</p>
          </div>
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="bg-red-500/20 border border-red-400 text-red-100 text-sm rounded-lg px-3 py-2">
                {error}
              </div>
            )}
            <div>
              <label className="block text-amber-100/90 text-sm font-medium mb-1">Username</label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="w-full bg-white/15 border border-white/30 rounded-lg px-4 py-2.5 text-white placeholder-amber-200/60 focus:outline-none focus:ring-2 focus:ring-amber-400"
                placeholder="e.g. afrobeats_fan"
                required
                minLength={3}
                autoComplete="username"
              />
              {usernameStatus !== 'idle' && (
                <p
                  className={`mt-1 text-xs ${
                    usernameStatus === 'available'
                      ? 'text-emerald-200'
                      : usernameStatus === 'checking'
                        ? 'text-amber-200'
                        : 'text-red-200'
                  }`}
                >
                  {usernameHint}
                </p>
              )}
            </div>
            <div>
              <label className="block text-amber-100/90 text-sm font-medium mb-1">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-white/15 border border-white/30 rounded-lg px-4 py-2.5 text-white placeholder-amber-200/60 focus:outline-none focus:ring-2 focus:ring-amber-400"
                placeholder="at least 6 characters"
                required
                minLength={6}
                autoComplete="new-password"
              />
            </div>
            <div>
              <label className="block text-amber-100/90 text-sm font-medium mb-1">Confirm password</label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full bg-white/15 border border-white/30 rounded-lg px-4 py-2.5 text-white placeholder-amber-200/60 focus:outline-none focus:ring-2 focus:ring-amber-400"
                placeholder="••••••••"
                required
                autoComplete="new-password"
              />
            </div>
            <button
              type="submit"
              disabled={loading || usernameStatus !== 'available'}
              className="w-full bg-white text-[#6B3B18] font-semibold py-2.5 rounded-lg hover:bg-amber-50 transition-colors disabled:opacity-50"
            >
              {loading ? 'Creating account…' : 'Create account'}
            </button>
          </form>
          <div className="mt-4 flex flex-col gap-2 text-center">
            <button
              type="button"
              onClick={() => navigate('/login')}
              className="text-amber-100/90 text-sm hover:text-white"
            >
              Already have an account? Sign in
            </button>
            <button
              type="button"
              onClick={() => navigate('/')}
              className="text-amber-100/90 text-sm hover:text-white"
            >
              ← Back to app
            </button>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
}
